import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import SEO from '@/components/SEO';
import { useBlogPost, useBlogAuthor, useBlogFeaturedImage, useBlogCategories } from '@/hooks/useBlog';
import { blogService } from '@/services/blogService';
import AutoLinkedContent from '@/components/blog/AutoLinkedContent';
import RelatedPosts from '@/components/blog/RelatedPosts';
import SocialShare from '@/components/blog/SocialShare';
import ReadingProgress from '@/components/blog/ReadingProgress';
import Comments from '@/components/blog/Comments';
import PillarPageBacklink from '@/components/blog/PillarPageBacklink';
import ArticleSchema from '@/components/seo/ArticleSchema';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  
  const { data: post, isLoading: postLoading, error: postError } = useBlogPost(slug || '');
  const { data: author } = useBlogAuthor(post?.author || 0);
  const { data: featuredImageUrl } = useBlogFeaturedImage(post);
  const { data: categories } = useBlogCategories();

  if (postLoading) {
    return (
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Skeleton className="h-4 w-24 mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48 mb-6" />
            <Skeleton className="h-64 w-full mb-8" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (postError || !post) {
    return (
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The blog post you're looking for doesn't exist or has been moved.
          </p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeftIcon className="mr-2 w-4 h-4" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const postCategories = categories?.filter(cat => post.categories.includes(cat.id)) || [];
  const readingTime = Math.ceil(blogService.stripHtml(post.content.rendered).split(' ').length / 200);
  const postUrl = `${window.location.origin}/blog/${post.slug}`;
  const postTitle = blogService.stripHtml(post.title.rendered);
  const postExcerpt = blogService.stripHtml(post.excerpt.rendered);
  const dateModified = (post as any)?.modified || post.date;

  return (
    <>
      <SEO 
        title={`${postTitle} - SeniorThrive Blog`}
        description={postExcerpt}
        keywords="senior blog, aging advice, wellness tips, health articles"
        image={featuredImageUrl || undefined}
        url={postUrl}
        type="article"
      />
      <ArticleSchema
        title={postTitle}
        description={postExcerpt}
        url={postUrl}
        image={featuredImageUrl || undefined}
        authorName={author?.name || 'SeniorThrive Team'}
        datePublished={post.date}
        dateModified={dateModified}
      />

      <ReadingProgress />
      
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          {/* Back to Blog */}
          <div className="mb-8">
            <Button variant="ghost" asChild>
              <Link to="/blog">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Back to Blog
              </Link>
            </Button>
          </div>

          {/* Article Header */}
          <PillarPageBacklink variant="banner" />
          
          <article className="mb-16">
            <header className="mb-8">
              {/* Categories */}
              {postCategories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {postCategories.map((category) => (
                    <Badge key={category.id} variant="secondary">
                      {category.name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                {postTitle}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-6">
                {author && (
                  <div className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-2" />
                    <span>{author.name}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  <span>{new Date(post.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  <span>{readingTime} min read</span>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            {featuredImageUrl && (
              <div className="mb-8">
                <img 
                  src={featuredImageUrl} 
                  alt={postTitle}
                  className="w-full max-h-96 object-contain rounded-lg bg-muted"
                />
              </div>
            )}

            {/* Content */}
            <AutoLinkedContent 
              content={post.content.rendered}
              className="prose prose-lg max-w-none 
                        prose-headings:text-foreground prose-headings:font-bold
                        prose-p:text-muted-foreground prose-p:leading-relaxed
                        prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                        prose-strong:text-foreground prose-strong:font-semibold
                        prose-ul:text-muted-foreground prose-ol:text-muted-foreground
                        prose-li:text-muted-foreground prose-li:my-1
                        prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground
                        prose-code:text-primary prose-code:bg-muted prose-code:px-1 prose-code:rounded
                        prose-img:rounded-lg prose-img:shadow-md prose-img:w-full prose-img:h-auto prose-img:object-contain prose-img:max-h-96 prose-img:bg-muted
                        dark:prose-invert"
            />
          </article>

          {/* Pillar Page Backlink */}
          <PillarPageBacklink variant="card" />

          {/* Social Share */}
          <SocialShare
            title={postTitle}
            url={postUrl}
            excerpt={postExcerpt}
          />


          {/* Comments */}
          <Comments postId={post.id} />

          {/* Related Posts */}
          <RelatedPosts currentPost={post} />

          {/* Back to Blog Footer */}
          <div className="border-t pt-8">
            <Button asChild>
              <Link to="/blog">
                <ArrowLeftIcon className="mr-2 w-4 h-4" />
                Back to All Articles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;